<template>
  <div class="error" v-if="errors[addressType].generic">{{ errors[addressType].generic }}</div>
  <ul class="list">
    <li
        role="radio"
        tabindex="0"
        :key="address.id"
        v-for="address in enrichedAddresses"
        :aria-checked="selectedAddressId === address.id"
        :class="{active: selectedAddressId === address.id}"
        :tabindex="selectedAddressId === address.id ? 0 : -1"
        @keydown.enter.prevent="handleCheckoutAddress(address)"
        @keydown.space.prevent="handleCheckoutAddress(address)"
    >
      <div class="tick" @click="handleCheckoutAddress(address)">
        <span class="iconfont-check" v-if="selectedAddressId === address.id"></span>
      </div>
      <div class="item-holder" @click="handleCheckoutAddress(address)">
        <div class="item-content">
          <div v-if="address.station_deliver === 'station'" class="station-title">{{ $t('address.dhl_station') }}</div>
          <div>{{ getGenderLabel(address.gender) }} {{ address.firstname }} {{ address.lastname }}</div>
          <div v-if="address.station_deliver === 'station'">{{ address.packstation }}, {{ address.postnumber }}</div>
          <div v-if="address.station_deliver !== 'station'">{{ address.company }} {{ address.address_details }}</div>
          <div v-if="address.station_deliver !== 'station'">{{ address.address }} {{ address.house_number }}</div>
          <div>{{ address.zip }} {{ address.city }}, {{ address.countryName }}</div>
        </div>
        <div class="error" v-if="address.is_invalid">{{ $t('shopping.errors.invalid_address') }}</div>
        <div class="actions">
          <a href="#" @click.prevent.stop="openAddressModal(address)">{{ $t('shopping.basket.checkout.edit') }}</a>
        </div>
      </div>
    </li>
  </ul>

  <BaseModal v-model="isModalOpen" class="address-modal">
    <template #header>
      <div class="modal-title">{{ $t('my_account.addresses.edit_address') }}</div>
    </template>
    <AddressForm v-model="formAddress" @submit="handleUpdateAddress" :showSave="true" @clearField="clearErrorField" :errors="errors[addressType]" />
  </BaseModal>
</template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { ref, computed } from 'vue'
import { scrollToError, showSnackbar } from '@/helper'
import { usePayment } from "@/composables/usePayment"
import BaseModal from "@/components/BaseModal/index.vue"
import { useUser } from "@/app/Checkout/composables/useUser"
import { useGender } from "@/app/Checkout/composables/useGender"
import { useCheckout } from "@/app/Checkout/composables/useCheckout"
import { useAddresses } from "@/app/Checkout/composables/useAddresses"
import AddressForm from "@/app/Checkout/components/common/AddressForm/index.vue"
import NBRadioGroup from "@/app/Checkout/components/common/NBRadioGroup/index.vue"
import { useAddressValidation } from "@/app/Checkout/composables/useAddressValidation"

const props = defineProps({
  addressType: {
    type: String,
    default: 'delivery_address',
  },
})

const addressError = ref('')
const isModalOpen = ref(false)
const { getGenderLabel } = useGender()
const { getPaymentMethods } = usePayment()
const { emptyAddress } = useAddressValidation()
const { getAddresses, updateAddress } = useUser()
const addressType = computed(() => props.addressType)
const { getCheckout, setShippingAddress, setInvoiceAddress } = useCheckout()
const { errors, setErrors, resetErrors, clearFieldError, mapAddressID, enrichedAddresses, isBillingAddressSame } = useAddresses()

const formAddress = ref(emptyAddress())
const selectedAddressId = computed(() => mapAddressID(props.addressType))

const clearErrorField = (field) => {
  clearFieldError({ field, type: props.addressType })
}

const handleKeydown = (e, index) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    const next = (index + 1) % enrichedAddresses.length
    handleCheckoutAddress(enrichedAddresses[next])
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault()
    const prev = (index - 1 + enrichedAddresses.length) % enrichedAddresses.length
    handleCheckoutAddress(enrichedAddresses[prev])
  }
}

function openAddressModal({phone_with_country_code, ...address}) {
  resetErrors(props.addressType)
  formAddress.value = { ...address }
  formAddress.value.phone = address.phone_with_country_code?.phone || address.phone || ''
  formAddress.value.phone_country_code = address.phone_with_country_code?.country || address.country || 'DE'
  isModalOpen.value = true
}

async function handleCheckoutAddress(address) {
  if (address.is_invalid) {
    return openAddressModal(address)
  }

  try {
    if (props.addressType === 'delivery_address') {
      const response = await setShippingAddress(address.id, isBillingAddressSame.value)

      if (response.redirect) {
        window.location.href = response.url

        return
      } else {
        await getPaymentMethods()
      }
    } else {
      await setInvoiceAddress(address.id)
    }

    await getCheckout()

    isModalOpen.value = false
  } catch(errors) {
    addressError.value = Array.isArray(errors) ? errors.join() : errors
    showSnackbar('error', 'iconfont-info', errors)
  }
}

async function handleUpdateAddress(address) {
  resetErrors(props.addressType)

  try {
    // 1. Update the address in the database
    await updateAddress({ id: address.id, data: address })

    // 2. Refresh the address list to get the latest data - small bogus delay afterwards
    await getAddresses()
    await new Promise(resolve => setTimeout(resolve, 100))

    // 3. Explicitly re-apply the address to the checkout state
    if (props.addressType === 'delivery_address') {
      await setShippingAddress(address.id, isBillingAddressSame.value)
      await getPaymentMethods()
    } else {
      await setInvoiceAddress(address.id)
    }

    // 4. Force a refresh of the checkout state
    await getCheckout()

    resetErrors(props.addressType)
    isModalOpen.value = false
  } catch (err) {
    setErrors({ errors: err?.errors ?? err, type: props.addressType })
  }
  scrollToError()
}
</script>
